/**
 * Sawyer 03 today-notes-board (C2, G8) — the visit's notes, called out on
 * the row the desk and the floor are already looking at.
 *
 * Sawyer (memo 2, 31:11, cleaned-up transcription): the notes "come out on
 * the run card, but that's sometimes a little too late … call out that there
 * is a note … like a booking note. Highlight." So the owner's words sit on
 * the arrival row, above Check In in reading order (Principle 2), and the
 * desk's same-day note sits under it.
 *
 * Renders NOTHING when there is no booking note and no desk note: never an
 * empty "Booking note" label (Principle 5), and an older server (keys absent)
 * gets today's row unchanged. Not a registry flag on purpose (a flag is
 * "visible anywhere that dog is seen"; a note is this visit's instruction),
 * so it never goes through DogFlagChips.
 *
 * Tokens: structural `--ferro-*` only (Principle 6). The micro-label carries
 * the meaning, not colour alone; a full 1px border, never a one-sided rail
 * (refuter C-5: a coloured side tab is an anti-pattern and collides with the
 * service-colour left rule on day-view rows).
 */
import { useState } from 'react';
import type { DeskNote } from '../dashboard/dashboard-api.js';
import './booking-notes.css';

/** Notes longer than this are clamped to three lines on screen with "Read all". */
export const NOTE_CLAMP_CHARS = 140;

export interface BookingNoteCalloutProps {
  readonly bookingNote: string | null | undefined;
  /** Parsed with `deskNotesOf`; null/undefined/[] all mean "none to show". */
  readonly deskNotes?: ReadonlyArray<DeskNote> | null;
  readonly testIdPrefix: string;
}

export function BookingNoteCallout({
  bookingNote,
  deskNotes,
  testIdPrefix,
}: BookingNoteCalloutProps): JSX.Element | null {
  const [expanded, setExpanded] = useState(false);
  const booking = typeof bookingNote === 'string' && bookingNote.trim().length > 0 ? bookingNote : null;
  const desk = (deskNotes ?? []).filter((d) => d.note.trim().length > 0);
  if (booking === null && desk.length === 0) return null;

  const long = [booking ?? '', ...desk.map((d) => d.note)].some((t) => t.length > NOTE_CLAMP_CHARS);
  const clampCls = long && !expanded ? ' ferro-booking-note__text--clamped' : '';
  const multiDog = desk.length > 1;

  return (
    <div className="ferro-booking-note" data-testid={testIdPrefix}>
      {booking !== null ? (
        <div className="ferro-booking-note__block">
          <p className="ferro-booking-note__label">Booking note</p>
          <p className={`ferro-booking-note__text${clampCls}`} data-testid={`${testIdPrefix}-booking`}>
            {booking}
          </p>
        </div>
      ) : null}
      {desk.map((d) => (
        <div className="ferro-booking-note__block" key={d.dogId}>
          <p className="ferro-booking-note__label">
            Desk note{multiDog ? <span className="ferro-booking-note__dog"> ({d.dogName})</span> : null}
          </p>
          <p className={`ferro-booking-note__text${clampCls}`} data-testid={`${testIdPrefix}-desk-${d.dogId}`}>
            {d.note}
          </p>
        </div>
      ))}
      {long ? (
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost ferro-booking-note__toggle"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          data-testid={`${testIdPrefix}-toggle`}
        >
          {expanded ? 'Show less' : 'Read all'}
        </button>
      ) : null}
    </div>
  );
}
